import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';

import { Spinner } from '/packages/shared-components/Spinner/Spinner';
import { Footer } from '/packages/shared-components/Footer/Footer';
import { useUser } from '~/src/utils/user';
import { validatePartnerId } from '/packages/shared-components/utils/util';
import { usePartnerContext } from '~/src/utils/partnerProvider';
import HeaderWrapper from '~src/components/navigation/HeaderWrapper/HeaderWrapper';
import { globalError } from '~src/utils/apollo';

import SWITCH_PARTNER from '~src/queries/codegen/switchPartner';

export interface PageProps {
  children: React.ReactNode;
  loading?: boolean;
}

export const BasePage = ({ children, loading = false }: PageProps) => {
  const { partnerId = '' } = useParams();
  const { currentUser, loadingUser } = useUser();
  const { partnerContext, setPartnerContext } = usePartnerContext();

  const [switchPartner] = useMutation(SWITCH_PARTNER, {
    variables: { partnerId },
    onCompleted: (data) => {
      data.switchPartner?.selectedPartner &&
        setPartnerContext({
          id: data.switchPartner?.selectedPartner?.id,
          name: data.switchPartner?.selectedPartner?.name,
        });
    },
  });

  useEffect(() => {
    if (partnerId && currentUser && validatePartnerId(currentUser, partnerId) && partnerId !== partnerContext.id) {
      switchPartner();
    }

    if (partnerId && currentUser && !validatePartnerId(currentUser, partnerId)) {
      globalError('forbidden');
    }
    // If user is in page without partnerId param, default to currentUser.selectedPartner
    if (!partnerId && !partnerContext.id && currentUser?.selectedPartner?.id) {
      switchPartner({ variables: { partnerId: currentUser.selectedPartner.id } });
    }
  }, [partnerId, currentUser, partnerContext, switchPartner]);

  return (
    <Stack sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <HeaderWrapper />
      <Stack component="main" sx={{ flexGrow: 1, pb: 4 }}>
        {loading || loadingUser ? <Spinner /> : children}
      </Stack>
      <Box component="footer">
        <Footer
          displaySize="responsive"
          countryCode={currentUser?.locationHeaders?.countryCode || null}
          // build={build?.webapp && !('error' in build.webapp) ? build.webapp : undefined}
        />
      </Box>
    </Stack>
  );
};
